import clsx from 'clsx';
import { CSSProperties, forwardRef, ReactNode } from 'react';
import { isEmptyObject } from '@srcDS/utils/assertions';

import mergeClasses from '../../../utils/shallowMerge';
import defaultClasses from './Heading.module.scss';

interface HeadingProps {
  as: any;
  styleAs?: string;
  size?: string;
  weight?: 'light' | 'normal' | 'semibold' | 'bold';
  placement?: string;
  children: ReactNode;
  className?: string;
  align?: string;
  textTransform?: string;
  customClasses?: Record<string, string>;
  inlineStyles?: CSSProperties;
  variables?: CSSProperties;
  [key: string]: any;
}

export const Heading = forwardRef<HTMLElement, HeadingProps>(
  (
    {
      as: Element = 'h1',
      styleAs,
      size,
      weight = 'normal',
      placement = 'common',
      children = '',
      className = '',
      align = 'start',
      textTransform = 'none',
      customClasses = null,
      inlineStyles,
      variables,
    },
    ref,
  ) => {
    const styles = mergeClasses(defaultClasses, customClasses);

    const headingClass = clsx(
      styles[`${styleAs || Element}`],
      styles[`${placement}_${styleAs || Element}`],
      {
        [`${styles[`size_${size}`]}`]: size,
        [`${styles[`weight_${weight}`]}`]: weight,
      },
      styles[`text_${textTransform}`],
      styles[`align_${align}`],
      styles[`common_${Element}`],
      className,
    );

    const transformChildren = (): ReactNode => {
      if (textTransform === 'firstletter' || typeof children === 'string') {
        // @ts-ignore
        return children.slice(0, 1).toUpperCase() + children.slice(1);
      }

      return children;
    };

    const fontWeights: Record<string, number> = {
      light: 300,
      normal: 400,
      semibold: 500,
      bold: 400,
    };

    const mergedStyles = {
      ...inlineStyles,
      '--default-font-weight': fontWeights[weight as keyof typeof fontWeights],
      '--default-text-transform': textTransform,
    };

    const combinedStyles = !isEmptyObject(variables) ? { ...mergedStyles, ...variables } : mergedStyles;

    return (
      // @ts-ignore
      <Element
        // @ts-ignore
        style={combinedStyles}
        ref={ref}
        className={headingClass}
      >
        {transformChildren()}
      </Element>
    );
  },
);

// Optional: Add display name for better debugging
Heading.displayName = 'Heading';
